import { useState, useEffect, useRef } from 'react'
import { useCascadeStore } from '../store'
import { DECISION_COLORS, type DecisionOutcome } from '../types'

const ARROW_SIZE = 10 // length of arrowhead

/** Compute a unit vector from (ax,ay) to (bx,by) */
function unitVec(ax: number, ay: number, bx: number, by: number) {
  const dx = bx - ax
  const dy = by - ay
  const len = Math.sqrt(dx * dx + dy * dy) || 1
  return { x: dx / len, y: dy / len }
}

/** Sample a cubic bezier at t to get the direction at t=1 */
function bezierTangentAtEnd(
  _x0: number, _y0: number,
  _cx1: number, _cy1: number,
  cx2x: number, cx2y: number,
  ex: number, ey: number,
) {
  // Tangent at t=1 is proportional to (end - control2)
  return unitVec(cx2x, cx2y, ex, ey)
}

/**
 * Measure the actual rendered size of a node's inner box (the rounded card).
 * The outer wrapper is an absolutely-positioned div; the first child is the
 * visible card with min-w / max-w constraints.
 * Falls back to sensible defaults when the element hasn't rendered yet.
 */
/** Measure the card element within a node. The card is the div with the rounded border
 *  (not the hero icon which sits above it). Returns width, height, and the Y offset
 *  of the card relative to the node wrapper (due to mt-7 for the hero icon). */
function measureNode(nodeId: string): { width: number; height: number; offsetY: number } {
  const el = document.querySelector(`[data-node-id="${nodeId}"]`) as HTMLElement | null
  if (el) {
    // The card is the last child element (the hero icon is first/second)
    const card = el.querySelector('[class*="rounded-xl"]') as HTMLElement | null
    if (card) {
      const w = card.offsetWidth
      const h = card.offsetHeight
      const offsetY = card.offsetTop
      if (w > 0 && h > 0) return { width: w, height: h, offsetY }
    }
  }
  return { width: 160, height: 65, offsetY: 28 }
}

export function CascadeEdges() {
  const { nodes, edges, removeEdge, updateEdge } = useCascadeStore()
  const [editingEdgeId, setEditingEdgeId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Force re-renders after DOM paints so measureNode picks up newly added/changed elements
  const [, setTick] = useState(0)
  useEffect(() => {
    // Double RAF to ensure layout has settled
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setTick(t => t + 1))
    })
    return () => cancelAnimationFrame(id)
  }, [nodes, edges])

  // Focus the input when editing starts
  useEffect(() => {
    if (editingEdgeId && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editingEdgeId])

  const commitEdit = () => {
    if (editingEdgeId) {
      updateEdge(editingEdgeId, { label: editText.trim() || undefined })
      setEditingEdgeId(null)
    }
  }

  const getNodeAnchor = (nodeId: string, side: 'left' | 'right' | 'bottom' | 'top') => {
    const node = nodes.find(n => n.id === nodeId)
    if (!node) return { x: 0, y: 0 }
    const { width, height, offsetY } = measureNode(nodeId)
    // Anchors are relative to the card, which is offset by the hero icon margin
    const cardY = node.y + offsetY
    switch (side) {
      case 'right':  return { x: node.x + width, y: cardY + height / 2 }
      case 'left':   return { x: node.x, y: cardY + height / 2 }
      case 'bottom': return { x: node.x + width / 2, y: cardY + height }
      case 'top':    return { x: node.x + width / 2, y: cardY }
    }
  }

  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 5, overflow: 'visible' }}>
      {edges.map(edge => {
        // Detect vertical edges (validation → secondary vendor, or any nodes stacked at same x)
        const fromNode = nodes.find(n => n.id === edge.from)
        const toNode = nodes.find(n => n.id === edge.to)
        const isSameXColumn = fromNode && toNode && Math.abs(fromNode.x - toNode.x) < 60 && toNode.y > fromNode.y
        const isVertical = isSameXColumn || (fromNode?.type === 'validation' && toNode?.type === 'validation' && toNode.isSecondaryVendor)

        // Simple vertical edge (validation → secondary vendor)
        if (isVertical) {
          const vFrom = getNodeAnchor(edge.from, 'bottom')
          const vTo = getNodeAnchor(edge.to, 'top')
          const baseY = vTo.y - ARROW_SIZE
          const pathD = `M ${vFrom.x} ${vFrom.y} L ${vTo.x} ${baseY}`
          const fullPath = `M ${vFrom.x} ${vFrom.y} L ${vTo.x} ${vTo.y}`
          const arrowPoints = `${vTo.x},${vTo.y} ${vTo.x - 5},${baseY} ${vTo.x + 5},${baseY}`
          return (
            <g key={edge.id}>
              <path
                d={fullPath}
                fill="none"
                stroke="transparent"
                strokeWidth={20}
                className="pointer-events-auto cursor-pointer"
                onContextMenu={(e) => { e.preventDefault(); removeEdge(edge.id) }}
              />
              <path
                d={pathD}
                fill="none"
                className="stroke-[var(--text-muted)]"
                strokeWidth={2}
              />
              <polygon points={arrowPoints} className="fill-[var(--text-muted)]" />
            </g>
          )
        }

        const from = getNodeAnchor(edge.from, 'right')
        const to = getNodeAnchor(edge.to, 'left')

        // Pull the endpoint back by ARROW_SIZE so the line stops at the arrowhead base
        const dir = unitVec(from.x, from.y, to.x, to.y)
        const lineEndX = to.x - dir.x * ARROW_SIZE
        const lineEndY = to.y - dir.y * ARROW_SIZE

        // Curved horizontal path
        const dx = lineEndX - from.x
        const midX1 = from.x + dx * 0.5
        const midX2 = from.x + dx * 0.5
        const pathD = `M ${from.x} ${from.y} C ${midX1} ${from.y}, ${midX2} ${lineEndY}, ${lineEndX} ${lineEndY}`
        const tang = bezierTangentAtEnd(from.x, from.y, midX1, from.y, midX2, lineEndY, lineEndX, lineEndY)

        const fullDx = to.x - from.x
        const fullMidX = from.x + fullDx * 0.5
        const fullPath = `M ${from.x} ${from.y} C ${fullMidX} ${from.y}, ${fullMidX} ${to.y}, ${to.x} ${to.y}`

        const perpX = -tang.y
        const perpY = tang.x
        const halfW = ARROW_SIZE * 0.45

        // Arrowhead triangle points: tip at `to`, base spread perpendicular to tangent
        const arrowPoints = [
          `${to.x},${to.y}`,
          `${lineEndX + perpX * halfW},${lineEndY + perpY * halfW}`,
          `${lineEndX - perpX * halfW},${lineEndY - perpY * halfW}`,
        ].join(' ')

        // Color based on condition
        let strokeClass = 'stroke-[var(--text-muted)]'
        let fillClass = 'fill-[var(--text-muted)]'
        if (edge.condition === 'pass') {
          strokeClass = 'stroke-emerald-500'
          fillClass = 'fill-emerald-500'
        } else if (edge.condition === 'fail' || edge.condition === 'no-match') {
          strokeClass = 'stroke-red-500'
          fillClass = 'fill-red-500'
        } else if (edge.condition === 'partial-match') {
          strokeClass = 'stroke-orange-500'
          fillClass = 'fill-orange-500'
        } else if (edge.condition === 'review') {
          strokeClass = 'stroke-amber-500'
          fillClass = 'fill-amber-500'
        }

        // Label position (midpoint)
        const labelX = (from.x + to.x) / 2
        const labelY = (from.y + to.y) / 2 - 14

        return (
          <g key={edge.id}>
            {/* Clickable area (wider invisible path for easy clicking) */}
            <path
              d={fullPath}
              fill="none"
              stroke="transparent"
              strokeWidth={20}
              className="pointer-events-auto cursor-pointer"
              onClick={() => {
                const conditions: (DecisionOutcome | undefined)[] = [undefined, 'pass', 'partial-match', 'no-match', 'fail']
                const currentIdx = conditions.indexOf(edge.condition)
                const nextIdx = (currentIdx + 1) % conditions.length
                updateEdge(edge.id, {
                  condition: conditions[nextIdx],
                  label: conditions[nextIdx] ? (DECISION_COLORS[conditions[nextIdx]!]?.label || conditions[nextIdx]) : undefined,
                })
              }}
              onDoubleClick={(e) => {
                e.stopPropagation()
                setEditText(edge.label || '')
                setEditingEdgeId(edge.id)
              }}
              onContextMenu={(e) => {
                e.preventDefault()
                removeEdge(edge.id)
              }}
            />

            {/* Visible line (stops at arrowhead base) */}
            <path
              d={pathD}
              fill="none"
              className={strokeClass}
              strokeWidth={2}
              strokeDasharray={edge.condition === 'partial-match' || edge.condition === 'no-match' ? '6,4' : undefined}
            />

            {/* Arrowhead triangle (drawn separately, no overlap) */}
            <polygon points={arrowPoints} className={fillClass} />

            {/* Edge label */}
            {edge.label && editingEdgeId !== edge.id && (
              <>
                <rect
                  x={labelX - 45}
                  y={labelY - 9}
                  width={90}
                  height={18}
                  rx={9}
                  className={
                    edge.condition === 'pass' ? 'fill-emerald-500/20' :
                    edge.condition === 'fail' || edge.condition === 'no-match' ? 'fill-red-500/20' :
                    edge.condition === 'partial-match' ? 'fill-orange-500/20' :
                    edge.condition === 'review' ? 'fill-amber-500/20' :
                    'fill-[var(--bg-tertiary)]'
                  }
                />
                <text
                  x={labelX}
                  y={labelY + 4}
                  textAnchor="middle"
                  className={`text-[10px] font-semibold ${
                    edge.condition === 'pass' ? 'fill-emerald-500' :
                    edge.condition === 'fail' || edge.condition === 'no-match' ? 'fill-red-500' :
                    edge.condition === 'partial-match' ? 'fill-orange-500' :
                    edge.condition === 'review' ? 'fill-amber-500' :
                    'fill-[var(--text-primary)]'
                  }`}
                >
                  {edge.label}
                </text>
              </>
            )}
            {/* Inline text editor for edge labels */}
            {editingEdgeId === edge.id && (
              <foreignObject
                x={labelX - 60}
                y={labelY - 14}
                width={120}
                height={28}
                className="pointer-events-auto"
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitEdit()
                    if (e.key === 'Escape') setEditingEdgeId(null)
                  }}
                  className="w-full h-full text-[10px] text-center font-semibold rounded-full bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] px-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Label..."
                />
              </foreignObject>
            )}
          </g>
        )
      })}
    </svg>
  )
}
